import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box, Button, styled } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { TSettingsFormData } from 'src/@types';
import FormProvider from 'src/components/hook-form';
import { Page } from 'src/components/Page';
import { useStores } from 'src/models';
import { settingsApi } from 'src/service';
import { useHandleResponse } from 'src/service/useHandleResponse';
import { getSettingsFormDefaultValues } from 'src/utils/getRHFDefaultValues';

import SettingsForm from './section/SettingsForm';

export const Settings = () => {
  const { t } = useTranslation();
  const handleResponse = useHandleResponse();
  const { loadingStore } = useStores();
  const methods = useForm<TSettingsFormData>({
    defaultValues: getSettingsFormDefaultValues(),
  });

  const { handleSubmit, reset } = methods;

  // 회사 설정 정보 조회 및 폼 초기화
  const initializeSettings = useCallback(async () => {
    try {
      loadingStore.setLoading(true);
      const result = await settingsApi.getSettings();

      if (result?.data) {
        const data = getSettingsFormDefaultValues(result.data);
        reset(data);
      }
    } catch (error) {
      console.log('설정을 불러오는데 실패했습니다.', error);
    } finally {
      loadingStore.setLoading(false);
    }
  }, [reset, loadingStore]);

  useEffect(() => {
    initializeSettings();
  }, [initializeSettings]);

  const onValid = async (data: TSettingsFormData) => {
    // formData 객체 생성
    const formData = {
      companyInfos: data.companyInfos.map((info) => ({
        ...info,
        langCd: info.langCd,
        logoImgFile: info.logoImgFile || null,
        locationImgFile: info.locationImgFile || null,
        companyIntroFile: info.companyIntroFile || null,
        logoImgPath: info.logoImgPath || null,
        locationImgPath: info.locationImgPath || null,
        companyIntroPath: info.companyIntroPath || null,
      })),
    };

    const result = await settingsApi.updateSettings(formData);

    // 결과 처리
    if (await result?.success) {
      handleResponse('저장이 완료되었습니다.', '저장이 실패되었습니다.');
    } else {
      console.error('저장 실패');
    }
  };

  return (
    <Page>
      <FormProvider methods={methods} onSubmit={handleSubmit(onValid)}>
        {/* 저장 버튼 */}
        <StyledHeader>
          <Button variant="contained" type="submit">
            {t('common.button.save')}
          </Button>
        </StyledHeader>
        {/* 좌측 설정 (한글) */}
        <StyledContent>
          <StyledSection>
            <StyledSectionTitle>{`[${t('settings.langCd.ko')}]`}</StyledSectionTitle>
            <SettingsForm langCd="ko" />
          </StyledSection>
          {/* 우측 설정 (영문) */}
          <StyledSection>
            <StyledSectionTitle>{`[${t('settings.langCd.en')}]`}</StyledSectionTitle>
            <SettingsForm langCd="en" />
          </StyledSection>
        </StyledContent>
      </FormProvider>
    </Page>
  );
};

export default observer(Settings);

const StyledHeader = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  padding: '10px 0',
});

const StyledContent = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: '24px',
  borderTop: `1px solid ${theme.palette.divider}`,
  '& > :first-of-type': {
    borderRight: `1px solid ${theme.palette.divider}`,
    paddingRight: '24px',
  },
}));

const StyledSection = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  height: '100%',
  overflow: 'auto',
  paddingBottom: '30px',
});

const StyledSectionTitle = styled(Box)({
  fontSize: '18px',
  fontWeight: 'bold',
  padding: '15px 0 8px 5px',
});
